import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';
import SidebarNav from '@/components/dashboard/SidebarNav';
import Topbar from '@/components/dashboard/Topbar';
import InstallPrompt from '@/components/dashboard/InstallPrompt';
import CalculatorPip from '@/components/tools/CalculatorPip';
import MilliDock from '@/components/milli/MilliDock';
import AccountStatusBanner from '@/components/dashboard/AccountStatusBanner';
import { DataProvider } from '@/context/DataContext';
import { useTheme } from '@/context/ThemeContext';
import { cn } from '@/lib/utils';

export const DashboardLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { theme } = useTheme();
  // Hybrid = light root with DARK chrome. Inject `.dark` onto the sidebar so it
  // resolves the dark palette while content + cards stay cream. (Hybrid Dark
  // already runs the whole root in `.dark`, so the chrome is dark for free.)
  const chromeDark = theme === 'hybrid';

  return (
    <DataProvider>
      <div className="min-h-screen bg-background text-foreground">
        <div className="safe-area-top-shield" aria-hidden="true" />
        <div className="flex min-h-screen">
          {/* Desktop sidebar */}
          <aside
            className={cn(
              'hidden lg:flex w-[280px] xl:w-[300px] shrink-0 border-r border-border',
              chromeDark && 'dark'
            )}
            data-testid="sidebar-desktop"
          >
            <SidebarNav idPrefix="sidebar" />
          </aside>

          {/* Main column */}
          <div className="flex-1 flex flex-col min-w-0">
            <Topbar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
            {/* pb-24 on phones/tablets: the floating calculator button
                (bottom-right, 48px) otherwise sits on top of the last row
                of every page with no way to scroll it clear. */}
            <main
              className="flex-1 p-4 pb-24 sm:p-6 sm:pb-24 lg:p-8 lg:pb-24 bg-background"
              data-testid="dashboard-main"
            >
              {/* Width cap keeps line lengths sane on ultrawides while letting
                  normal desktops use the whole screen. 1400 read as a dead
                  column beside the sidebar on wide monitors — 1680 fills them
                  without turning table rows into runways.
                  xl:pr-12 (48px) reserves clearance for the Milli dock's
                  collapsed handle (components/milli/MilliDock.js, fixed to
                  the right edge at xl+) so wide-screen panels — the
                  master-detail split, sticky totals rail, etc. — never sit
                  underneath it. */}
              <div className="mx-auto w-full max-w-[1680px] xl:pr-12">
                <AccountStatusBanner />
                <Outlet />
              </div>
            </main>
          </div>
        </div>
        {/* Post-login "Add to home screen" nudge — authenticated area only. */}
        <InstallPrompt />
        {/* Draggable PIP calculator — available across the dashboard. */}
        <CalculatorPip />
        {/* Milli dock — K.O. Command chat on every page (owner-only,
            desktop-only; self-contained, owned by the Command track). */}
        <MilliDock />
      </div>
    </DataProvider>
  );
};

export default DashboardLayout;
